'use client';

import React from 'react';
import { useRouter } from '@/lib/router';
import { plans, formatRupiah } from '@/lib/mock-data';
import { AnimatedCounter, StatsCard } from '@/components/shared';

// ============================================
// 1. LANDING PAGE
// ============================================

export function LandingPage() {
  const navigate = useRouter((s) => s.navigate);

  const features = [
    {
      icon: '🤖',
      title: 'AI Agent Otomatis',
      desc: 'Buat asisten AI cerdas yang menjawab pelanggan 24/7 tanpa libur. Tingkatkan respon hingga 10x lipat.',
    },
    {
      icon: '📱',
      title: 'Multi-Channel',
      desc: 'Hubungkan WhatsApp, Telegram, dan Website dalam satu dashboard. Satu agent untuk semua channel.',
    },
    {
      icon: '🧠',
      title: 'Knowledge Base RAG',
      desc: 'Upload katalog produk, FAQ, dan dokumen bisnis. AI akan belajar dan menjawab sesuai data Anda.',
    },
    {
      icon: '📊',
      title: 'Analytics Dashboard',
      desc: 'Pantau performa bisnis real-time. Lihat jumlah percakapan, pelanggan baru, dan tren penjualan.',
    },
    {
      icon: '🏪',
      title: 'Modul Bisnis',
      desc: 'Template khusus untuk Retail, FnB, dan Rental/Sewa. Mulai lebih cepat dengan modul siap pakai.',
    },
    {
      icon: '🔗',
      title: 'Integrasi & API',
      desc: 'Hubungkan dengan Google Sheets, webhook, dan API kustom. Otomasi tanpa batas untuk bisnis Anda.',
    },
  ];

  const steps = [
    {
      num: '01',
      title: 'Daftar & Buat Workspace',
      desc: 'Buat akun gratis, masukkan nama bisnis, dan pilih jenis usaha Anda. Hanya butuh 2 menit.',
    },
    {
      num: '02',
      title: 'Hubungkan WA & Buat Agent',
      desc: 'Scan QR WhatsApp, upload katalog produk, dan atur AI Agent sesuai kebutuhan bisnis Anda.',
    },
    {
      num: '03',
      title: 'Otomatiskan & Monitor',
      desc: 'Agent Anda langsung bekerja 24/7! Pantau performa lewat dashboard analytics yang intuitif.',
    },
  ];

  const testimonials = [
    {
      name: 'Budi Santoso',
      role: 'Pemilik Toko Budi Pratama',
      text: 'Sejak pakai Rekanku, respon pelanggan naik 300%. Staf bisa fokus packing order sementara AI yang handle tanya jawab pelanggan.',
      avatar: 'BS',
    },
    {
      name: 'Sari Dewi',
      role: 'Owner Warung Sari Catering',
      text: 'Awalnya ragu, tapi setelah trial langsung berlangganan Pro. AI Agent kami bisa handle 80% pertanyaan pelanggan tanpa campur tangan.',
      avatar: 'SD',
    },
    {
      name: 'Ahmad Fauzi',
      role: 'CEO Rental Mobil Jaya',
      text: 'Knowledge Base RAG-nya luar biasa. Pelanggan bisa cek ketersediaan dan harga sendiri lewat WA. Pemesanan jadi lebih efisien!',
      avatar: 'AF',
    },
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="relative py-24 md:py-32 px-6 overflow-hidden">
        {/* Background gradient */}
        <div className="absolute inset-0 bg-gradient-to-b from-orange-600/10 via-slate-900 to-slate-900" />
        <div className="absolute top-20 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-orange-500/8 rounded-full blur-3xl" />

        <div className="relative max-w-7xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-orange-500/10 border border-orange-500/20 rounded-full px-4 py-1.5 mb-8">
            <span className="w-2 h-2 rounded-full bg-orange-400 animate-pulse" />
            <span className="text-sm text-orange-400 font-medium">Baru — Fitur AI Agent v2.0 telah rilis</span>
          </div>

          <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold leading-tight mb-6">
            Rekan AI Terbaik untuk{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-amber-400">
              Bisnis yang Lebih Cerdas 🔥
            </span>
          </h1>

          <p className="text-lg md:text-xl text-slate-400 max-w-2xl mx-auto mb-10 leading-relaxed">
            Otomatisasi WhatsApp, Telegram, dan Website bisnis Anda dengan AI Agent cerdas.
            Tangani pelanggan 24/7 tanpa tambah staf.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16">
            <button
              onClick={() => navigate('/register')}
              className="w-full sm:w-auto px-8 py-4 bg-orange-600 hover:bg-orange-700 text-white text-lg font-semibold rounded-xl transition-all hover:shadow-lg hover:shadow-orange-600/25 hover:-translate-y-0.5"
            >
              Coba Gratis 14 Hari →
            </button>
            <button
              onClick={() => navigate('/about')}
              className="w-full sm:w-auto px-8 py-4 border border-slate-700 hover:border-slate-500 text-slate-200 text-lg font-semibold rounded-xl transition-all hover:-translate-y-0.5"
            >
              Lihat Demo ▷
            </button>
          </div>

          {/* Social Proof */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-6 text-slate-500">
            <div className="flex -space-x-2">
              {['AS', 'BS', 'CD', 'DE', 'EF'].map((initials, i) => (
                <div
                  key={i}
                  className="w-9 h-9 rounded-full bg-slate-700 border-2 border-slate-900 flex items-center justify-center text-xs font-bold text-slate-300"
                >
                  {initials}
                </div>
              ))}
            </div>
            <p className="text-sm">
              Dipercaya oleh <span className="text-slate-200 font-semibold">2,500+ UMKM</span> di Indonesia
            </p>
            <div className="hidden sm:flex items-center gap-1">
              {'★★★★★'.split('').map((star, i) => (
                <span key={i} className="text-amber-400 text-sm">{star}</span>
              ))}
              <span className="text-sm text-slate-400 ml-1">4.9/5</span>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section id="features" className="py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-sm font-semibold text-orange-400 uppercase tracking-wider mb-3">Fitur Unggulan</p>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Semua yang Bisnis Anda Butuhkan
            </h2>
            <p className="text-slate-400 max-w-xl mx-auto">
              Platform lengkap untuk mengelola komunikasi pelanggan dan otomasi bisnis dengan kecerdasan buatan.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, i) => (
              <div
                key={i}
                className="group bg-slate-800/50 border border-slate-700/50 rounded-2xl p-6 hover:border-orange-500/30 hover:bg-slate-800 transition-all duration-300"
              >
                <div className="w-12 h-12 rounded-xl bg-orange-500/10 flex items-center justify-center text-2xl mb-4 group-hover:scale-110 transition-transform">
                  {feature.icon}
                </div>
                <h3 className="text-lg font-semibold text-slate-100 mb-2">{feature.title}</h3>
                <p className="text-sm text-slate-400 leading-relaxed">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it Works */}
      <section className="py-24 px-6 bg-slate-800/30">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-sm font-semibold text-orange-400 uppercase tracking-wider mb-3">Cara Kerja</p>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Mulai dalam 3 Langkah Mudah
            </h2>
            <p className="text-slate-400 max-w-xl mx-auto">
              Tanpa instalasi rumit. Langsung pakai dalam hitungan menit.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {steps.map((step, i) => (
              <div key={i} className="relative text-center">
                {i < steps.length - 1 && (
                  <div className="hidden md:block absolute top-10 left-[60%] w-[80%] h-px border-t-2 border-dashed border-slate-700" />
                )}
                <div className="relative inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-orange-600 text-white text-2xl font-bold mb-6 shadow-lg shadow-orange-600/20">
                  {step.num}
                </div>
                <h3 className="text-lg font-semibold text-slate-100 mb-2">{step.title}</h3>
                <p className="text-sm text-slate-400 leading-relaxed max-w-xs mx-auto">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="text-center p-6">
              <div className="text-4xl md:text-5xl font-extrabold text-slate-50 mb-2">
                <AnimatedCounter target={2500} suffix="+" />
              </div>
              <p className="text-sm text-slate-400">Tenant Aktif</p>
            </div>
            <div className="text-center p-6">
              <div className="text-4xl md:text-5xl font-extrabold text-slate-50 mb-2">
                <AnimatedCounter target={50000} suffix="+" />
              </div>
              <p className="text-sm text-slate-400">Percakapan/bulan</p>
            </div>
            <div className="text-center p-6">
              <div className="text-4xl md:text-5xl font-extrabold text-slate-50 mb-2">
                <AnimatedCounter target={999} suffix="%" />
              </div>
              <p className="text-sm text-slate-400">Uptime</p>
            </div>
            <div className="text-center p-6">
              <div className="text-4xl md:text-5xl font-extrabold text-amber-400 mb-2">
                <AnimatedCounter target={49} suffix="★" />
              </div>
              <p className="text-sm text-slate-400">Rating</p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Preview */}
      <section className="py-24 px-6 bg-slate-800/30">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-sm font-semibold text-orange-400 uppercase tracking-wider mb-3">Harga</p>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Paket untuk Setiap Skala Bisnis
            </h2>
            <p className="text-slate-400 max-w-xl mx-auto">
              Mulai dari Rp 99rb/bulan. Coba gratis 14 hari tanpa kartu kredit.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {plans.map((plan) => (
              <div
                key={plan.id}
                className={`relative bg-slate-800 border rounded-2xl p-6 transition-all hover:-translate-y-1 ${
                  plan.is_popular
                    ? 'border-orange-500 shadow-lg shadow-orange-500/10'
                    : 'border-slate-700 hover:border-slate-600'
                }`}
              >
                {plan.is_popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-orange-500 text-white text-xs font-semibold px-3 py-1 rounded-full">
                    Populer
                  </div>
                )}
                <h3 className="text-lg font-semibold text-slate-100 mb-1">{plan.name}</h3>
                <div className="mb-4">
                  <span className="text-3xl font-extrabold text-slate-50">{formatRupiah(plan.price_monthly)}</span>
                  <span className="text-sm text-slate-500">/bulan</span>
                </div>
                <ul className="space-y-2 mb-6">
                  {plan.features.slice(0, 5).map((f, fi) => (
                    <li key={fi} className="flex items-center gap-2 text-sm text-slate-300">
                      <span className="text-emerald-400 text-xs">✓</span> {f}
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => navigate('/pricing')}
                  className={`w-full py-2.5 rounded-xl text-sm font-semibold transition-all ${
                    plan.is_popular
                      ? 'bg-orange-600 hover:bg-orange-700 text-white'
                      : 'border border-slate-600 hover:border-orange-500 text-slate-300 hover:text-orange-400'
                  }`}
                >
                  Mulai Gratis
                </button>
              </div>
            ))}
          </div>

          <div className="text-center mt-8">
            <button
              onClick={() => navigate('/pricing')}
              className="text-sm text-orange-400 hover:text-orange-300 font-medium transition-colors"
            >
              Lihat detail perbandingan semua paket →
            </button>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-sm font-semibold text-orange-400 uppercase tracking-wider mb-3">Testimoni</p>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Apa Kata Mereka?
            </h2>
            <p className="text-slate-400 max-w-xl mx-auto">
              Ribuan pelaku UMKM telah merasakan manfaat Rekanku untuk bisnis mereka.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.map((t, i) => (
              <div key={i} className="bg-slate-800 border border-slate-700 rounded-2xl p-6 hover:border-slate-600 transition-all">
                <div className="flex items-center gap-1 mb-4">
                  {'★★★★★'.split('').map((star, si) => (
                    <span key={si} className="text-amber-400 text-sm">{star}</span>
                  ))}
                </div>
                <p className="text-sm text-slate-300 leading-relaxed mb-6">&ldquo;{t.text}&rdquo;</p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-orange-500/20 flex items-center justify-center text-sm font-bold text-orange-400">
                    {t.avatar}
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-slate-100">{t.name}</p>
                    <p className="text-xs text-slate-500">{t.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="relative bg-gradient-to-br from-orange-600 via-orange-500 to-amber-500 rounded-3xl p-10 md:p-16 text-center overflow-hidden">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(255,255,255,0.1),transparent)]" />
            <div className="relative">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                Siap Membawa Bisnis Anda ke Level Berikutnya?
              </h2>
              <p className="text-orange-100 mb-8 max-w-xl mx-auto">
                Bergabung dengan 2,500+ UMKM yang sudah menggunakan Rekanku untuk mengotomatisasi bisnis mereka.
              </p>
              <button
                onClick={() => navigate('/register')}
                className="px-8 py-4 bg-white text-orange-600 text-lg font-semibold rounded-xl hover:bg-orange-50 transition-all hover:shadow-lg hover:-translate-y-0.5"
              >
                Mulai Gratis Sekarang →
              </button>
              <p className="text-sm text-orange-200 mt-4">Tanpa kartu kredit • Setup 2 menit • Batal kapan saja</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

// ============================================
// 2. ABOUT PAGE
// ============================================

export function AboutPage() {
  const navigate = useRouter((s) => s.navigate);

  const values = [
    {
      icon: '🏪',
      title: 'Pro-UMKM',
      desc: 'Kami membangun Rekanku khusus untuk pelaku usaha kecil dan menengah Indonesia. Setiap fitur dirancang agar mudah dipahami oleh siapa saja.',
    },
    {
      icon: '🔒',
      title: 'Keamanan Utama',
      desc: 'Data pelanggan Anda adalah prioritas kami. Semua data dienkripsi dan disimpan di server yang aman. Kami memenuhi standar keamanan internasional.',
    },
    {
      icon: '💡',
      title: 'Selalu Inovatif',
      desc: 'Teknologi AI terus berkembang dan kami selalu mengikuti perkembangan terbaru. Kami berkomitmen memberikan fitur terbaik untuk bisnis Anda.',
    },
  ];

  const techStack = [
    { name: 'OpenAI GPT-4o', icon: '🤖' },
    { name: 'WhatsApp Baileys', icon: '📱' },
    { name: 'Next.js 16', icon: '⚡' },
    { name: 'React', icon: '⚛️' },
    { name: 'PostgreSQL', icon: '🐘' },
    { name: 'Redis', icon: '🔴' },
    { name: 'Midtrans', icon: '💳' },
    { name: 'Vercel', icon: '▲' },
  ];

  return (
    <div>
      {/* Hero */}
      <section className="relative py-24 md:py-32 px-6 overflow-hidden">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-orange-500/10 rounded-full blur-3xl" />
        <div className="relative max-w-4xl mx-auto text-center">
          <p className="text-sm font-semibold text-orange-400 uppercase tracking-wider mb-3">Tentang Kami</p>
          <h1 className="text-4xl md:text-5xl font-extrabold mb-6">
            Tentang{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-amber-400">
              Rekanku
            </span>
          </h1>
          <p className="text-lg text-slate-400 max-w-2xl mx-auto leading-relaxed">
            Kami percaya setiap UMKM di Indonesia berhak punya akses ke teknologi AI untuk mengembangkan bisnis mereka.
            Rekanku hadir sebagai rekan perjalanan digital Anda.
          </p>
        </div>
      </section>

      {/* Misi & Visi */}
      <section className="py-24 px-6 bg-slate-800/30">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-slate-800 border border-slate-700 rounded-2xl p-8">
              <div className="w-12 h-12 rounded-xl bg-orange-500/10 flex items-center justify-center text-2xl mb-4">🎯</div>
              <h3 className="text-xl font-bold text-slate-50 mb-3">Misi Kami</h3>
              <p className="text-slate-400 leading-relaxed">
                Menyediakan platform AI yang terjangkau dan mudah digunakan untuk setiap pelaku UMKM di Indonesia,
                sehingga mereka dapat bersaing dengan bisnis besar melalui otomasi cerdas.
              </p>
            </div>
            <div className="bg-slate-800 border border-slate-700 rounded-2xl p-8">
              <div className="w-12 h-12 rounded-xl bg-amber-500/10 flex items-center justify-center text-2xl mb-4">🔭</div>
              <h3 className="text-xl font-bold text-slate-50 mb-3">Visi Kami</h3>
              <p className="text-slate-400 leading-relaxed">
                Menjadi platform AI terdepan yang memberdayakan 60 juta+ UMKM Indonesia untuk bertransformasi digital
                dan meningkatkan produktivitas bisnis secara signifikan.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Cerita Kami */}
      <section className="py-24 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-sm font-semibold text-orange-400 uppercase tracking-wider mb-3">Cerita Kami</p>
          <h2 className="text-3xl font-bold mb-8">Dari Masalah Nyata</h2>
          <div className="space-y-4 text-slate-400 leading-relaxed">
            <p>
              Rekanku lahir dari pengalaman langsung melihat pelaku UMKM yang kewalahan menangani ribuan pesan
              pelanggan setiap hari. Staf toko harus bolak-balik antara melayani pelanggan offline dan menjawab chat,
              sehingga banyak pesan yang terlewat dan pelanggan kecewa.
            </p>
            <p>
              Kami membangun Rekanku dengan misi sederhana: membiarkan AI menangani pertanyaan rutin, sehingga pemilik
              bisnis bisa fokus pada hal yang lebih penting — mengembangkan produk dan melayani pelanggan dengan lebih baik.
            </p>
            <p>
              Sejak diluncurkan pada tahun 2025, Rekanku telah dipercaya oleh lebih dari 2,500 pelaku UMKM di seluruh Indonesia
              dan telah memproses lebih dari 50,000 percakapan per bulan.
            </p>
          </div>
        </div>
      </section>

      {/* Nilai-Nilai */}
      <section className="py-24 px-6 bg-slate-800/30">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-sm font-semibold text-orange-400 uppercase tracking-wider mb-3">Nilai-Nilai</p>
            <h2 className="text-3xl font-bold">Yang Kami Pegang</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {values.map((v, i) => (
              <div key={i} className="bg-slate-800 border border-slate-700 rounded-2xl p-6 hover:border-orange-500/30 transition-all text-center">
                <div className="w-14 h-14 rounded-2xl bg-orange-500/10 flex items-center justify-center text-3xl mx-auto mb-4">
                  {v.icon}
                </div>
                <h3 className="text-lg font-semibold text-slate-100 mb-3">{v.title}</h3>
                <p className="text-sm text-slate-400 leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-24 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-slate-800 border border-slate-700 rounded-2xl p-8 text-center">
              <div className="text-4xl font-extrabold text-slate-50 mb-2">
                <AnimatedCounter target={2500} suffix="+" />
              </div>
              <p className="text-sm text-slate-400">Tenant Aktif</p>
            </div>
            <div className="bg-slate-800 border border-slate-700 rounded-2xl p-8 text-center">
              <div className="text-4xl font-extrabold text-amber-400 mb-2">2025</div>
              <p className="text-sm text-slate-400">Didirikan</p>
            </div>
            <div className="bg-slate-800 border border-slate-700 rounded-2xl p-8 text-center">
              <div className="text-4xl font-extrabold text-slate-50 mb-2">
                <AnimatedCounter target={999} suffix="%" />
              </div>
              <p className="text-sm text-slate-400">Uptime</p>
            </div>
          </div>
        </div>
      </section>

      {/* Tech Stack */}
      <section className="py-24 px-6 bg-slate-800/30">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-sm font-semibold text-orange-400 uppercase tracking-wider mb-3">Teknologi</p>
          <h2 className="text-3xl font-bold mb-10">Stack yang Kami Gunakan</h2>
          <div className="flex flex-wrap items-center justify-center gap-4">
            {techStack.map((tech, i) => (
              <div
                key={i}
                className="flex items-center gap-2 bg-slate-800 border border-slate-700 rounded-full px-5 py-2.5 hover:border-orange-500/30 transition-all"
              >
                <span className="text-lg">{tech.icon}</span>
                <span className="text-sm font-medium text-slate-300">{tech.name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-4">Bergabung dengan Kami</h2>
          <p className="text-slate-400 mb-8">Mulai perjalanan digital bisnis Anda hari ini. Gratis 14 hari.</p>
          <button
            onClick={() => navigate('/register')}
            className="px-8 py-4 bg-orange-600 hover:bg-orange-700 text-white text-lg font-semibold rounded-xl transition-all hover:shadow-lg hover:shadow-orange-600/25 hover:-translate-y-0.5"
          >
            Mulai Gratis →
          </button>
        </div>
      </section>
    </div>
  );
}

// ============================================
// 3. PRICING PAGE
// ============================================

export function PricingPage() {
  const navigate = useRouter((s) => s.navigate);
  const [isYearly, setIsYearly] = React.useState(false);
  const [openFaq, setOpenFaq] = React.useState<number | null>(null);

  const faqs = [
    {
      q: 'Apakah saya bisa mencoba sebelum berlangganan?',
      a: 'Ya! Semua paket memiliki masa trial gratis 14 hari. Anda bisa mencoba semua fitur tanpa perlu kartu kredit. Jika tidak puas, Anda bisa membatalkan kapan saja.',
    },
    {
      q: 'Apa perbedaan paket Bulanan dan Tahunan?',
      a: 'Paket tahunan memberikan diskon 20% dibandingkan paket bulanan. Anda membayar di muka untuk 12 bulan dan hemat hingga Rp 1.4 juta untuk paket Business.',
    },
    {
      q: 'Bisa upgrade atau downgrade paket kapan saja?',
      a: 'Tentu! Anda bisa upgrade atau downgrade paket kapan saja melalui dashboard. Perubahan akan diterapkan di periode billing berikutnya dengan prorata.',
    },
    {
      q: 'Bagaimana cara pembayaran?',
      a: 'Kami menerima pembayaran melalui transfer bank (BCA, Mandiri, BNI, BRI), e-wallet (GoPay, OVO, DANA), kartu kredit/debit, dan QRIS melalui Midtrans.',
    },
    {
      q: 'Apakah ada batasan jumlah pelanggan?',
      a: 'Tidak ada batasan jumlah pelanggan di semua paket. Perbedaan paket terletak pada jumlah AI Agent, sesi WhatsApp, dan kapasitas token AI per bulan.',
    },
  ];

  const allFeatures = [
    { name: 'AI Agent', starter: '1', pro: '5', business: 'Unlimited' },
    { name: 'Sesi WhatsApp', starter: '1', pro: '3', business: '10' },
    { name: 'Token AI / bulan', starter: '10K', pro: '50K', business: '200K' },
    { name: 'Knowledge Base', starter: '100MB', pro: '500MB', business: '2GB' },
    { name: 'Analytics Dashboard', starter: false, pro: true, business: true },
    { name: 'Google Sheets Integration', starter: false, pro: true, business: true },
    { name: 'White Label', starter: false, pro: false, business: true },
    { name: 'Custom Integration', starter: false, pro: false, business: true },
    { name: 'Priority Support', starter: false, pro: true, business: true },
    { name: 'Dedicated Support', starter: false, pro: false, business: true },
  ];

  return (
    <div>
      {/* Hero */}
      <section className="relative py-24 md:py-32 px-6">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] bg-orange-500/8 rounded-full blur-3xl" />
        <div className="relative max-w-4xl mx-auto text-center">
          <p className="text-sm font-semibold text-orange-400 uppercase tracking-wider mb-3">Harga</p>
          <h1 className="text-4xl md:text-5xl font-extrabold mb-6">
            Harga Transparan untuk Setiap{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-amber-400">
              Skala Bisnis
            </span>
          </h1>
          <p className="text-lg text-slate-400 max-w-xl mx-auto">
            Mulai dari Rp 99rb/bulan. Tanpa biaya tersembunyi. Coba gratis 14 hari.
          </p>
        </div>
      </section>

      {/* Toggle & Cards */}
      <section className="pb-24 px-6 -mt-8">
        <div className="max-w-6xl mx-auto">
          {/* Billing Toggle */}
          <div className="flex items-center justify-center gap-4 mb-12">
            <span className={`text-sm font-medium transition-colors ${!isYearly ? 'text-slate-100' : 'text-slate-500'}`}>Bulanan</span>
            <button
              onClick={() => setIsYearly(!isYearly)}
              className={`relative w-14 h-7 rounded-full transition-colors ${isYearly ? 'bg-orange-600' : 'bg-slate-700'}`}
            >
              <div
                className={`absolute top-0.5 w-6 h-6 rounded-full bg-white shadow transition-transform ${isYearly ? 'translate-x-7.5' : 'translate-x-0.5'}`}
              />
            </button>
            <span className={`text-sm font-medium transition-colors ${isYearly ? 'text-slate-100' : 'text-slate-500'}`}>
              Tahunan
              <span className="ml-2 text-xs text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded-full">Hemat 20%</span>
            </span>
          </div>

          {/* Plan Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {plans.map((plan) => {
              const price = isYearly ? plan.price_yearly / 12 : plan.price_monthly;
              return (
                <div
                  key={plan.id}
                  className={`relative bg-slate-800 border rounded-2xl p-8 transition-all hover:-translate-y-1 ${
                    plan.is_popular
                      ? 'border-orange-500 shadow-xl shadow-orange-500/10 scale-[1.02] md:scale-105'
                      : 'border-slate-700 hover:border-slate-600'
                  }`}
                >
                  {plan.is_popular && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-orange-500 text-white text-xs font-semibold px-4 py-1 rounded-full">
                      ★ Populer
                    </div>
                  )}
                  <h3 className="text-xl font-bold text-slate-100 mb-1">{plan.name}</h3>
                  <p className="text-sm text-slate-500 mb-4">
                    Cocok untuk {plan.name === 'Starter' ? 'bisnis yang baru mulai' : plan.name === 'Pro' ? 'bisnis yang berkembang' : 'bisnis skala besar'}
                  </p>
                  <div className="mb-6">
                    {isYearly && (
                      <p className="text-xs text-slate-500 line-through mb-1">{formatRupiah(plan.price_monthly)}/bulan</p>
                    )}
                    <span className="text-4xl font-extrabold text-slate-50">{formatRupiah(price)}</span>
                    <span className="text-sm text-slate-500">/bulan</span>
                  </div>
                  <button
                    onClick={() => navigate('/checkout/' + plan.id)}
                    className={`w-full py-3 rounded-xl text-sm font-semibold transition-all mb-6 ${
                      plan.is_popular
                        ? 'bg-orange-600 hover:bg-orange-700 text-white shadow-lg shadow-orange-600/20'
                        : 'border border-slate-600 hover:border-orange-500 text-slate-300 hover:text-orange-400'
                    }`}
                  >
                    Pilih {plan.name}
                  </button>
                  <ul className="space-y-3">
                    {plan.features.map((f, fi) => (
                      <li key={fi} className="flex items-start gap-2 text-sm">
                        <span className="text-emerald-400 mt-0.5 flex-shrink-0">✓</span>
                        <span className="text-slate-300">{f}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="py-24 px-6 bg-slate-800/30">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold">Perbandingan Fitur Lengkap</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-700">
                  <th className="text-left py-4 px-4 text-slate-400 font-medium">Fitur</th>
                  <th className="text-center py-4 px-4 text-slate-400 font-medium">Starter</th>
                  <th className="text-center py-4 px-4 text-orange-400 font-medium">Pro ★</th>
                  <th className="text-center py-4 px-4 text-slate-400 font-medium">Business</th>
                </tr>
              </thead>
              <tbody>
                {allFeatures.map((f, i) => (
                  <tr key={i} className="border-b border-slate-700/50">
                    <td className="py-3 px-4 text-slate-300">{f.name}</td>
                    <td className="py-3 px-4 text-center">
                      {f.starter === false ? (
                        <span className="text-slate-600">✕</span>
                      ) : (
                        <span className="text-slate-200">{f.starter}</span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-center bg-orange-500/5">
                      {f.pro === false ? (
                        <span className="text-slate-600">✕</span>
                      ) : f.pro === true ? (
                        <span className="text-emerald-400">✓</span>
                      ) : (
                        <span className="text-slate-200">{f.pro}</span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-center">
                      {f.business === false ? (
                        <span className="text-slate-600">✕</span>
                      ) : f.business === true ? (
                        <span className="text-emerald-400">✓</span>
                      ) : (
                        <span className="text-slate-200">{f.business}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 px-6">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Pertanyaan Umum</h2>
            <p className="text-slate-400">Belum menemukan jawaban? Hubungi kami di support@rekanku.id</p>
          </div>
          <div className="space-y-3">
            {faqs.map((faq, i) => (
              <div key={i} className="bg-slate-800 border border-slate-700 rounded-xl overflow-hidden">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between p-5 text-left"
                >
                  <span className="text-sm font-medium text-slate-200 pr-4">{faq.q}</span>
                  <svg
                    className={`w-5 h-5 text-slate-500 flex-shrink-0 transition-transform ${openFaq === i ? 'rotate-180' : ''}`}
                    fill="none" viewBox="0 0 24 24" stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {openFaq === i && (
                  <div className="px-5 pb-5">
                    <p className="text-sm text-slate-400 leading-relaxed">{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 px-6 bg-slate-800/30">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-4">Siap Memulai?</h2>
          <p className="text-slate-400 mb-8">Coba gratis 14 hari. Tanpa kartu kredit. Setup dalam 2 menit.</p>
          <button
            onClick={() => navigate('/register')}
            className="px-8 py-4 bg-orange-600 hover:bg-orange-700 text-white text-lg font-semibold rounded-xl transition-all hover:shadow-lg hover:shadow-orange-600/25"
          >
            Daftar Gratis Sekarang →
          </button>
        </div>
      </section>
    </div>
  );
}

// ============================================
// 4. LOGIN PAGE
// ============================================

export function LoginPage() {
  const navigate = useRouter((s) => s.navigate);
  const [showPassword, setShowPassword] = React.useState(false);
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    navigate('/setup-workspace');
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 items-center justify-center p-12">
        <div className="max-w-md">
          <button onClick={() => navigate('/')} className="flex items-center gap-2 text-3xl font-bold mb-12">
            <span className="text-4xl">🔥</span>
            <span className="text-orange-500">Rekanku</span>
          </button>
          <h2 className="text-3xl font-bold text-slate-50 mb-4">
            Selamat Datang Kembali! 👋
          </h2>
          <p className="text-slate-400 mb-10 leading-relaxed">
            Masuk ke dashboard Anda untuk mengelola AI Agent, memantau percakapan pelanggan, dan mengoptimalkan bisnis.
          </p>
          <div className="space-y-4">
            {[
              { icon: '🤖', text: 'Monitor AI Agent 24/7' },
              { icon: '📊', text: 'Analytics real-time bisnis Anda' },
              { icon: '💬', text: 'Kelola semua percakapan pelanggan' },
              { icon: '📱', text: 'Kelola channel WA & Telegram' },
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-orange-500/10 flex items-center justify-center text-lg">
                  {item.icon}
                </div>
                <span className="text-sm text-slate-300">{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="flex-1 flex items-center justify-center p-6 md:p-12 bg-slate-900">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-2 text-2xl font-bold mb-8">
            <span className="text-3xl">🔥</span>
            <span className="text-orange-500">Rekanku</span>
          </div>

          <h1 className="text-2xl font-bold text-slate-50 mb-2">Masuk ke Akun Anda</h1>
          <p className="text-sm text-slate-400 mb-8">
            Belum punya akun?{' '}
            <button onClick={() => navigate('/register')} className="text-orange-400 hover:text-orange-300 font-medium transition-colors">
              Daftar Gratis →
            </button>
          </p>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1.5">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="nama@bisnis.com"
                className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-3 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500/50 transition-all"
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="block text-sm font-medium text-slate-300">Password</label>
                <button type="button" onClick={() => navigate('/forgot-password')} className="text-xs text-orange-400 hover:text-orange-300 transition-colors">
                  Lupa password?
                </button>
              </div>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Masukkan password"
                  className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-3 pr-11 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500/50 transition-all"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 transition-colors"
                >
                  {showPassword ? (
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" /></svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                  )}
                </button>
              </div>
            </div>

            <button
              type="submit"
              className="w-full py-3 bg-orange-600 hover:bg-orange-700 text-white font-semibold rounded-xl transition-all hover:shadow-lg hover:shadow-orange-600/20"
            >
              Masuk
            </button>
          </form>

          <div className="relative my-8">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-slate-700" /></div>
            <div className="relative flex justify-center"><span className="bg-slate-900 px-3 text-sm text-slate-500">atau masuk dengan</span></div>
          </div>

          <button
            onClick={() => navigate('/setup-workspace')}
            className="w-full flex items-center justify-center gap-3 py-3 border border-slate-700 hover:border-slate-500 rounded-xl text-sm font-medium text-slate-300 hover:text-white transition-all"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" /><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" /><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" /><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" /></svg>
            Masuk dengan Google
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================
// 5. REGISTER PAGE
// ============================================

export function RegisterPage() {
  const navigate = useRouter((s) => s.navigate);
  const [showPassword, setShowPassword] = React.useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = React.useState(false);
  const [form, setForm] = React.useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    referralCode: '',
  });

  const updateForm = (key: string, value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    navigate('/setup-workspace');
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-orange-600 via-orange-500 to-amber-500 items-center justify-center p-12 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_80%,rgba(255,255,255,0.1),transparent)]" />
        <div className="relative max-w-md text-white">
          <button onClick={() => navigate('/')} className="flex items-center gap-2 text-3xl font-bold mb-12">
            <span className="text-4xl">🔥</span>
            <span className="text-white">Rekanku</span>
          </button>
          <h2 className="text-3xl font-bold mb-4">
            Mulai Gratis, Tanpa Kartu Kredit 🚀
          </h2>
          <p className="text-orange-100 mb-10 leading-relaxed">
            Daftar sekarang dan dapatkan akses penuh selama 14 hari gratis. Rasakan bagaimana AI dapat mengubah bisnis Anda.
          </p>
          <div className="space-y-4">
            {[
              { icon: '✅', text: '14 hari trial gratis, tanpa kartu kredit' },
              { icon: '🤖', text: 'Setup AI Agent dalam 2 menit' },
              { icon: '📱', text: 'Hubungkan WhatsApp langsung' },
              { icon: '📊', text: 'Dashboard analytics real-time' },
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-3">
                <span className="text-xl">{item.icon}</span>
                <span className="text-sm text-orange-100">{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="flex-1 flex items-center justify-center p-6 md:p-12 bg-slate-900">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-2 text-2xl font-bold mb-8">
            <span className="text-3xl">🔥</span>
            <span className="text-orange-500">Rekanku</span>
          </div>

          <h1 className="text-2xl font-bold text-slate-50 mb-2">Buat Akun Baru</h1>
          <p className="text-sm text-slate-400 mb-8">
            Sudah punya akun?{' '}
            <button onClick={() => navigate('/login')} className="text-orange-400 hover:text-orange-300 font-medium transition-colors">
              Masuk di sini →
            </button>
          </p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1.5">Nama Lengkap</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => updateForm('name', e.target.value)}
                placeholder="Budi Santoso"
                className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-3 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500/50 transition-all"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1.5">Email</label>
              <input
                type="email"
                value={form.email}
                onChange={(e) => updateForm('email', e.target.value)}
                placeholder="nama@bisnis.com"
                className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-3 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500/50 transition-all"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1.5">Password</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={form.password}
                  onChange={(e) => updateForm('password', e.target.value)}
                  placeholder="Min. 8 karakter"
                  className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-3 pr-11 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500/50 transition-all"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 transition-colors"
                >
                  {showPassword ? '🙈' : '👁'}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1.5">Konfirmasi Password</label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={form.confirmPassword}
                  onChange={(e) => updateForm('confirmPassword', e.target.value)}
                  placeholder="Ulangi password"
                  className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-3 pr-11 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500/50 transition-all"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 transition-colors"
                >
                  {showConfirmPassword ? '🙈' : '👁'}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1.5">
                Kode Referral{' '}
                <span className="text-slate-500 font-normal">(opsional)</span>
              </label>
              <input
                type="text"
                value={form.referralCode}
                onChange={(e) => updateForm('referralCode', e.target.value)}
                placeholder="Masukkan kode referral"
                className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-3 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500/50 transition-all"
              />
            </div>

            <button
              type="submit"
              className="w-full py-3 bg-orange-600 hover:bg-orange-700 text-white font-semibold rounded-xl transition-all hover:shadow-lg hover:shadow-orange-600/20 mt-2"
            >
              Daftar Gratis
            </button>
          </form>

          <p className="text-xs text-slate-500 text-center mt-6">
            Dengan mendaftar, Anda menyetujui{' '}
            <span className="text-slate-400 underline cursor-pointer">Syarat & Ketentuan</span> dan{' '}
            <span className="text-slate-400 underline cursor-pointer">Kebijakan Privasi</span> kami.
          </p>
        </div>
      </div>
    </div>
  );
}

// ============================================
// 6. CHECKOUT PAGE
// ============================================

export function CheckoutPage() {
  const navigate = useRouter((s) => s.navigate);
  const params = useRouter((s) => s.params);
  const [paymentMethod, setPaymentMethod] = React.useState('bank_transfer');
  const [promoCode, setPromoCode] = React.useState('');

  const planId = params.id || '2';
  const selectedPlan = plans.find((p) => String(p.id) === planId) || plans[1];

  return (
    <div className="min-h-screen bg-slate-900 flex flex-col">
      {/* Minimal Navbar */}
      <nav className="h-16 border-b border-slate-800 flex items-center px-6 bg-slate-900 sticky top-0 z-50">
        <button onClick={() => navigate('/')} className="flex items-center gap-2 text-xl font-bold">
          <span className="text-2xl">🔥</span>
          <span className="text-orange-500">Rekanku</span>
        </button>
      </nav>

      {/* Content */}
      <main className="flex-1 py-12 px-6">
        <div className="max-w-5xl mx-auto">
          <button
            onClick={() => navigate('/pricing')}
            className="flex items-center gap-2 text-sm text-slate-400 hover:text-slate-200 mb-8 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
            Kembali ke Harga
          </button>

          <h1 className="text-2xl font-bold text-slate-50 mb-2">Checkout</h1>
          <p className="text-sm text-slate-400 mb-10">Review pesanan Anda sebelum melakukan pembayaran.</p>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
            {/* Left - Order Details */}
            <div className="lg:col-span-3 space-y-6">
              {/* Invoice Info */}
              <div className="bg-slate-800 border border-slate-700 rounded-2xl p-6">
                <h3 className="text-lg font-semibold text-slate-100 mb-4">Informasi Pesanan</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">Invoice</label>
                    <p className="text-sm text-slate-300">INV-2025-NEW-001</p>
                  </div>
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">Tanggal</label>
                    <p className="text-sm text-slate-300">15 Januari 2025</p>
                  </div>
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">Email</label>
                    <p className="text-sm text-slate-300">user@email.com</p>
                  </div>
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">Periode</label>
                    <p className="text-sm text-slate-300">15 Jan - 15 Feb 2025</p>
                  </div>
                </div>
              </div>

              {/* Order Items */}
              <div className="bg-slate-800 border border-slate-700 rounded-2xl p-6">
                <h3 className="text-lg font-semibold text-slate-100 mb-4">Item Pesanan</h3>
                <div className="flex items-center justify-between py-3 border-b border-slate-700/50">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-orange-500/10 flex items-center justify-center text-lg">📦</div>
                    <div>
                      <p className="text-sm font-medium text-slate-200">Paket {selectedPlan.name}</p>
                      <p className="text-xs text-slate-500">1 bulan langganan</p>
                    </div>
                  </div>
                  <p className="text-sm font-semibold text-slate-200">{formatRupiah(selectedPlan.price_monthly)}</p>
                </div>
                <div className="flex items-center justify-between py-3 border-b border-slate-700/50">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center text-lg">🎁</div>
                    <div>
                      <p className="text-sm font-medium text-slate-200">Bonus Trial 14 Hari</p>
                      <p className="text-xs text-slate-500">Diperpanjang otomatis</p>
                    </div>
                  </div>
                  <p className="text-sm font-semibold text-emerald-400">Gratis</p>
                </div>
              </div>

              {/* Payment Method */}
              <div className="bg-slate-800 border border-slate-700 rounded-2xl p-6">
                <h3 className="text-lg font-semibold text-slate-100 mb-4">Metode Pembayaran</h3>
                <div className="space-y-3">
                  {[
                    { id: 'bank_transfer', label: 'Transfer Bank', desc: 'BCA, Mandiri, BNI, BRI', icon: '🏦' },
                    { id: 'ewallet', label: 'GoPay / OVO / DANA', desc: 'E-wallet instant', icon: '📱' },
                    { id: 'credit_card', label: 'Kartu Kredit / Debit', desc: 'Visa, Mastercard, JCB', icon: '💳' },
                    { id: 'qris', label: 'QRIS', desc: 'Scan QR dari aplikasi bank manapun', icon: '📷' },
                  ].map((method) => (
                    <label
                      key={method.id}
                      className={`flex items-center gap-4 p-4 rounded-xl border cursor-pointer transition-all ${
                        paymentMethod === method.id
                          ? 'border-orange-500 bg-orange-500/5'
                          : 'border-slate-700 hover:border-slate-600'
                      }`}
                    >
                      <input
                        type="radio"
                        name="payment"
                        value={method.id}
                        checked={paymentMethod === method.id}
                        onChange={(e) => setPaymentMethod(e.target.value)}
                        className="sr-only"
                      />
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                        paymentMethod === method.id ? 'border-orange-500' : 'border-slate-600'
                      }`}>
                        {paymentMethod === method.id && <div className="w-2.5 h-2.5 rounded-full bg-orange-500" />}
                      </div>
                      <span className="text-xl">{method.icon}</span>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-slate-200">{method.label}</p>
                        <p className="text-xs text-slate-500">{method.desc}</p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            {/* Right - Summary */}
            <div className="lg:col-span-2">
              <div className="bg-slate-800 border border-slate-700 rounded-2xl p-6 sticky top-24">
                <h3 className="text-lg font-semibold text-slate-100 mb-6">Ringkasan Pembayaran</h3>

                <div className="space-y-3 mb-6">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400">Subtotal</span>
                    <span className="text-slate-200">{formatRupiah(selectedPlan.price_monthly)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400">PPN (11%)</span>
                    <span className="text-slate-200">{formatRupiah(Math.round(selectedPlan.price_monthly * 0.11))}</span>
                  </div>
                  <div className="border-t border-slate-700 pt-3 flex justify-between">
                    <span className="text-sm font-semibold text-slate-100">Total</span>
                    <span className="text-lg font-bold text-orange-400">{formatRupiah(Math.round(selectedPlan.price_monthly * 1.11))}</span>
                  </div>
                </div>

                {/* Promo Code */}
                <div className="mb-6">
                  <label className="block text-xs text-slate-500 mb-2">Kode Promo</label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={promoCode}
                      onChange={(e) => setPromoCode(e.target.value)}
                      placeholder="Masukkan kode"
                      className="flex-1 bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500/50 transition-all"
                    />
                    <button className="px-4 py-2 border border-slate-600 hover:border-orange-500 text-sm text-slate-300 hover:text-orange-400 rounded-lg transition-all whitespace-nowrap">
                      Pakai
                    </button>
                  </div>
                </div>

                <button
                  onClick={() => navigate('/setup-workspace')}
                  className="w-full py-3.5 bg-orange-600 hover:bg-orange-700 text-white font-semibold rounded-xl transition-all hover:shadow-lg hover:shadow-orange-600/20 flex items-center justify-center gap-2"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>
                  Bayar Sekarang
                </button>

                <div className="flex items-center justify-center gap-2 mt-4 text-xs text-slate-500">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>
                  Pembayaran aman via Midtrans
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

// ============================================
// 7. SETUP WORKSPACE PAGE
// ============================================

export function SetupWorkspacePage() {
  const navigate = useRouter((s) => s.navigate);
  const [step, setStep] = React.useState(1);
  const [form, setForm] = React.useState({
    businessName: '',
    slug: '',
    businessType: '',
    planId: '',
  });
  const [slugChecked, setSlugChecked] = React.useState(false);

  const businessTypes = [
    { id: 'retail', icon: '🏪', title: 'Retail', desc: 'Toko online, fashion, elektronik, dll.' },
    { id: 'fnb', icon: '🍽', title: 'Restoran / FnB', desc: 'Warung, kafe, catering, restoran.' },
    { id: 'rental', icon: '🚗', title: 'Rental / Sewa', desc: 'Sewa mobil, alat, property, dll.' },
  ];

  const updateForm = (key: string, value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    if (key === 'businessName') {
      const slug = value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
      updateForm('slug', slug);
      setSlugChecked(false);
    }
  };

  const canProceed = () => {
    if (step === 1) return form.businessName.length >= 3 && form.slug.length >= 3;
    if (step === 2) return form.businessType !== '';
    if (step === 3) return form.planId !== '';
    return false;
  };

  const handleNext = () => {
    if (step < 3) setStep(step + 1);
    else navigate('/tenant/dashboard');
  };

  const handleBack = () => {
    if (step > 1) setStep(step - 1);
    else navigate('/login');
  };

  return (
    <div className="min-h-screen bg-slate-900 flex flex-col">
      {/* Navbar */}
      <nav className="h-16 border-b border-slate-800 flex items-center px-6 bg-slate-900 sticky top-0 z-50">
        <button onClick={() => navigate('/')} className="flex items-center gap-2 text-xl font-bold">
          <span className="text-2xl">🔥</span>
          <span className="text-orange-500">Rekanku</span>
        </button>
      </nav>

      {/* Content */}
      <main className="flex-1 py-12 px-6">
        <div className="max-w-2xl mx-auto">
          {/* Step Indicator */}
          <div className="flex items-center justify-center gap-0 mb-12">
            {[
              { num: 1, label: 'Workspace' },
              { num: 2, label: 'Jenis Bisnis' },
              { num: 3, label: 'Pilih Plan' },
            ].map((s, i) => (
              <React.Fragment key={s.num}>
                <div className="flex items-center gap-3">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold transition-all ${
                      step >= s.num
                        ? 'bg-orange-600 text-white shadow-lg shadow-orange-600/20'
                        : 'bg-slate-800 text-slate-500 border border-slate-700'
                    }`}
                  >
                    {step > s.num ? '✓' : s.num}
                  </div>
                  <span className={`text-sm font-medium hidden sm:block ${step >= s.num ? 'text-slate-100' : 'text-slate-500'}`}>
                    {s.label}
                  </span>
                </div>
                {i < 2 && (
                  <div className={`w-12 sm:w-24 h-0.5 mx-2 sm:mx-4 rounded ${step > s.num ? 'bg-orange-600' : 'bg-slate-700'}`} />
                )}
              </React.Fragment>
            ))}
          </div>

          {/* Step 1: Workspace */}
          {step === 1 && (
            <div className="bg-slate-800 border border-slate-700 rounded-2xl p-8">
              <div className="text-center mb-8">
                <div className="w-16 h-16 rounded-2xl bg-orange-500/10 flex items-center justify-center text-3xl mx-auto mb-4">🏢</div>
                <h2 className="text-2xl font-bold text-slate-50 mb-2">Buat Workspace Anda</h2>
                <p className="text-sm text-slate-400">Workspace adalah ruang kerja untuk bisnis Anda di Rekanku.</p>
              </div>

              <div className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1.5">Nama Bisnis</label>
                  <input
                    type="text"
                    value={form.businessName}
                    onChange={(e) => updateForm('businessName', e.target.value)}
                    placeholder="contoh: Toko Budi Pratama"
                    className="w-full bg-slate-900 border border-slate-700 rounded-xl px-4 py-3 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500/50 transition-all"
                  />
                  <p className="text-xs text-slate-500 mt-1.5">Nama yang akan tampil di dashboard dan chat pelanggan.</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1.5">URL Slug</label>
                  <div className="flex gap-2">
                    <div className="flex items-center bg-slate-900 border border-slate-700 rounded-xl px-3 py-1 text-xs text-slate-500 whitespace-nowrap">
                      rekanku.id/
                    </div>
                    <div className="flex-1 flex gap-2">
                      <input
                        type="text"
                        value={form.slug}
                        onChange={(e) => updateForm('slug', e.target.value)}
                        placeholder="toko-budi-pratama"
                        className="flex-1 bg-slate-900 border border-slate-700 rounded-xl px-4 py-3 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500/50 transition-all"
                      />
                      <button
                        onClick={() => setSlugChecked(true)}
                        disabled={form.slug.length < 3}
                        className="px-4 py-3 border border-slate-600 hover:border-orange-500 disabled:border-slate-700 disabled:text-slate-600 text-sm text-slate-300 hover:text-orange-400 rounded-xl transition-all whitespace-nowrap"
                      >
                        Check
                      </button>
                    </div>
                  </div>
                  {slugChecked && form.slug.length >= 3 && (
                    <p className="text-xs text-emerald-400 mt-1.5">✓ Slug &quot;{form.slug}&quot; tersedia!</p>
                  )}
                  <p className="text-xs text-slate-500 mt-1.5">URL unik untuk bisnis Anda. Hanya huruf kecil, angka, dan tanda hubung.</p>
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Business Type */}
          {step === 2 && (
            <div className="bg-slate-800 border border-slate-700 rounded-2xl p-8">
              <div className="text-center mb-8">
                <div className="w-16 h-16 rounded-2xl bg-orange-500/10 flex items-center justify-center text-3xl mx-auto mb-4">🏪</div>
                <h2 className="text-2xl font-bold text-slate-50 mb-2">Pilih Jenis Bisnis</h2>
                <p className="text-sm text-slate-400">Kami akan mengoptimalkan fitur sesuai jenis bisnis Anda.</p>
              </div>

              <div className="space-y-4">
                {businessTypes.map((bt) => (
                  <label
                    key={bt.id}
                    className={`flex items-center gap-4 p-5 rounded-xl border-2 cursor-pointer transition-all ${
                      form.businessType === bt.id
                        ? 'border-orange-500 bg-orange-500/5'
                        : 'border-slate-700 hover:border-slate-600'
                    }`}
                  >
                    <input
                      type="radio"
                      name="businessType"
                      value={bt.id}
                      checked={form.businessType === bt.id}
                      onChange={(e) => updateForm('businessType', e.target.value)}
                      className="sr-only"
                    />
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-2xl ${
                      form.businessType === bt.id ? 'bg-orange-500/20' : 'bg-slate-700/50'
                    }`}>
                      {bt.icon}
                    </div>
                    <div className="flex-1">
                      <p className="text-base font-semibold text-slate-100">{bt.title}</p>
                      <p className="text-sm text-slate-400">{bt.desc}</p>
                    </div>
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                      form.businessType === bt.id ? 'border-orange-500' : 'border-slate-600'
                    }`}>
                      {form.businessType === bt.id && <div className="w-2.5 h-2.5 rounded-full bg-orange-500" />}
                    </div>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Step 3: Plan */}
          {step === 3 && (
            <div className="bg-slate-800 border border-slate-700 rounded-2xl p-8">
              <div className="text-center mb-8">
                <div className="w-16 h-16 rounded-2xl bg-orange-500/10 flex items-center justify-center text-3xl mx-auto mb-4">💎</div>
                <h2 className="text-2xl font-bold text-slate-50 mb-2">Pilih Paket Anda</h2>
                <p className="text-sm text-slate-400">Semua paket termasuk trial 14 hari gratis. Anda bisa upgrade nanti.</p>
              </div>

              <div className="space-y-4">
                {plans.map((plan) => (
                  <label
                    key={plan.id}
                    className={`flex items-center gap-4 p-5 rounded-xl border-2 cursor-pointer transition-all ${
                      form.planId === String(plan.id)
                        ? 'border-orange-500 bg-orange-500/5'
                        : plan.is_popular
                          ? 'border-slate-600 hover:border-orange-500/50'
                          : 'border-slate-700 hover:border-slate-600'
                    }`}
                  >
                    <input
                      type="radio"
                      name="plan"
                      value={plan.id}
                      checked={form.planId === String(plan.id)}
                      onChange={(e) => updateForm('planId', e.target.value)}
                      className="sr-only"
                    />
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-xl font-bold ${
                      form.planId === String(plan.id) ? 'bg-orange-500/20 text-orange-400' : 'bg-slate-700/50 text-slate-400'
                    }`}>
                      {plan.name[0]}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-0.5">
                        <p className="text-base font-semibold text-slate-100">{plan.name}</p>
                        {plan.is_popular && (
                          <span className="text-xs bg-orange-500 text-white px-2 py-0.5 rounded-full">Populer</span>
                        )}
                      </div>
                      <p className="text-xs text-slate-400">{plan.features.slice(0, 3).join(' • ')}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold text-slate-100">{formatRupiah(plan.price_monthly)}</p>
                      <p className="text-xs text-slate-500">/bulan</p>
                    </div>
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                      form.planId === String(plan.id) ? 'border-orange-500' : 'border-slate-600'
                    }`}>
                      {form.planId === String(plan.id) && <div className="w-2.5 h-2.5 rounded-full bg-orange-500" />}
                    </div>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Navigation */}
          <div className="flex items-center justify-between mt-8">
            <button
              onClick={handleBack}
              className="flex items-center gap-2 text-sm text-slate-400 hover:text-slate-200 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
              {step === 1 ? 'Kembali' : '← Kembali'}
            </button>
            <button
              onClick={handleNext}
              disabled={!canProceed()}
              className="px-8 py-3 bg-orange-600 hover:bg-orange-700 disabled:bg-slate-700 disabled:text-slate-500 text-white font-semibold rounded-xl transition-all hover:shadow-lg hover:shadow-orange-600/20 disabled:shadow-none flex items-center gap-2"
            >
              {step === 3 ? 'Buat Workspace & Mulai →' : 'Lanjut →'}
            </button>
          </div>
        </div>
      </main>
    </div>
  );
}
